import {
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Button,
  Paper,
  Divider,
  Box,
  Avatar,
  Link,
  Alert,
} from '@mui/material'
import mapping from 'atoms/Icon/mapping'
import Loading from 'atoms/Loading'
import { InstallationDetail, InstallationErrorPayload } from 'common/types/kraken-core/Installation'
import { IntegrationModule } from 'common/types/kraken-core/IntegrationModule'
import { useEffect, useState } from 'react'
import { ROUTES } from 'routing/routes'
import {
  getIntegrationModule,
  installIntegrationModuleViaSelfService,
} from 'services/kraken-core/integration_module/integration.service'
import InstallationErrorOutput from './InstallationErrorOutput'
import ParametersEditor from './ParametersEditor'

export interface IntegrationModuleInstallationWizardProps {
  integrationModuleId: string
  onClose: () => void
  onSuccess: () => void
  onError: (error: string) => void
}

const STEPS = ['Parameters', 'Confirm', 'Finish'] as const

function formatParameterValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export default function IntegrationModuleInstallationWizard({
  integrationModuleId,
  onClose,
  onSuccess,
  onError,
}: IntegrationModuleInstallationWizardProps) {
  const [integrationModule, setIntegrationModule] = useState<IntegrationModule | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [activeStep, setActiveStep] = useState<number>(0)
  const [parameters, setParameters] = useState<Record<string, unknown>>({})
  const [isInstalling, setIsInstalling] = useState(false)
  const [installation, setInstallation] = useState<InstallationDetail | null>(null)
  const [installError, setInstallError] = useState<InstallationErrorPayload | null>(null)

  const handleBack = () => {
    if (isInstalling) return
    setActiveStep(prevStep => Math.max(prevStep - 1, 0))
  }

  const handleInstall = async () => {
    setIsInstalling(true)
    setInstallError(null)
    setInstallation(null)
    setActiveStep(2)

    try {
      const { error, response } = await installIntegrationModuleViaSelfService(
        integrationModuleId,
        parameters
      )

      if (error) {
        setInstallError(error)
        return
      }

      setInstallation(response.data)
    } catch {
      setInstallError({ message: 'Installation failed' })
    } finally {
      setIsInstalling(false)
    }
  }

  const handlePrimaryAction = () => {
    if (activeStep === 0) {
      setActiveStep(1)
      return
    }
    if (activeStep === 1) {
      handleInstall()
      return
    }
    if (installError) {
      onClose()
      return
    }
    onSuccess()
  }

  useEffect(() => {
    setIsLoading(true)
    getIntegrationModule(integrationModuleId)
      .then(response => {
        if (response.response) {
          setIntegrationModule(response.response.data)
        } else {
          onError('Failed to fetch integration module')
        }
      })
      .catch(() => {
        onError('Failed to fetch integration module')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [integrationModuleId])

  const parameterEntries = Object.entries(parameters)
  const primaryLabel =
    activeStep === 0 ? 'Next' : activeStep === 1 ? 'Install' : installError ? 'Close' : 'Done'

  return (
    <Paper
      sx={{
        position: 'relative',
        minWidth: '80%',
        minHeight: '80%',
        maxWidth: '80%',
        maxHeight: '80%',
        overflow: 'hidden',
        boxShadow: 24,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isLoading && (
        <Box p={2}>
          <Loading />
        </Box>
      )}
      {!isLoading && !integrationModule && (
        <Box p={2}>
          <p>Integration module not found</p>
        </Box>
      )}

      {!isLoading && integrationModule ? (
        <>
          <Box
            flexDirection={'row'}
            display="flex"
            alignItems="center"
            sx={{
              p: 2,
            }}
          >
            <Avatar
              sizes="large"
              sx={{
                backgroundColor: 'secondary.main',
              }}
            >
              {mapping['IntegrationModule']}
            </Avatar>
            <Stack sx={{ pl: 2 }}>
              <Typography variant="caption" fontWeight={'bold'}>
                Installation
              </Typography>
              <Typography variant="h2" fontWeight={'bold'} sx={{ flexShrink: 0 }}>
                {integrationModule?.name || 'Integration Module'}
              </Typography>
            </Stack>
          </Box>

          <Divider />

          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{ width: '100%', pt: 2, flexShrink: 0 }}
          >
            {STEPS.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Stack sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {activeStep === 0 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <ParametersEditor
                  schema={integrationModule?.metadata.input_schema}
                  uiSchema={integrationModule?.metadata.ui_schema}
                  data={parameters}
                  onChange={setParameters}
                />
              </Paper>
            )}

            {activeStep === 1 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Review parameters
                </Typography>
                {parameterEntries.length === 0 ? (
                  <Typography color="text.secondary">No parameters provided.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {parameterEntries.map(([key, value]) => (
                      <Box key={key}>
                        <Typography variant="caption" color="text.secondary">
                          {key}
                        </Typography>
                        <Typography variant="body1">{formatParameterValue(value)}</Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Paper>
            )}

            {activeStep === 2 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                {isInstalling && <Loading className="w-full flex justify-center items-center" />}
                {!isInstalling && installError && <InstallationErrorOutput error={installError} />}
                {!isInstalling && installation && !installError && (
                  <Stack spacing={1}>
                    <Alert severity="success">Integration module installed successfully.</Alert>
                    <Typography variant="body2">
                      Installation:{' '}
                      <Link
                        href={`${ROUTES.INSTALLATIONS}/${installation.id}`}
                        target="_blank"
                        rel="noopener"
                      >
                        {installation.name}
                      </Link>
                    </Typography>
                  </Stack>
                )}
              </Paper>
            )}
          </Stack>
          <Divider />
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'white',
              p: 2,
              flexShrink: 0,
            }}
          >
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Button
                variant="contained"
                disabled={activeStep === 0 || (activeStep === 2 && !installError)}
                onClick={handleBack}
              >
                Previous
              </Button>
              <Button variant="contained" disabled={isInstalling} onClick={handlePrimaryAction}>
                {isInstalling ? 'Installing…' : primaryLabel}
              </Button>
            </Stack>
          </Box>
        </>
      ) : null}
    </Paper>
  )
}
